// apps/web/src/features/kronos/timer/DailyProgressBar.tsx
import { useEffect, useState } from 'react';
import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../core/theme/theme';

const DAILY_TARGET_SECONDS = 7 * 3600; // 7h
const WORKDAY_START_HOUR = 8;  // 08:00
const WORKDAY_END_HOUR = 19;   // 19:00
const WORKDAY_SPAN_HOURS = WORKDAY_END_HOUR - WORKDAY_START_HOUR; // 11h window

interface DailyProgressBarProps {
  /** Sum of today's saved entries in seconds. */
  totalSeconds: number;
  /** Live timer elapsed in milliseconds (0 if no timer running). */
  timerElapsedMs: number;
}

function formatHoursMinutes(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return `${h}h${m.toString().padStart(2, '0')}`;
}

/**
 * Compute how far through the workday we are as a percentage of the
 * 7h target bar. Before 09:00 → 0%, after 17:00 → 100%, linearly
 * interpolated in between. This maps the 8h workday window onto the
 * 7h target bar so the marker shows "ideal progress if working steadily".
 */
function computeNowPercent(): number {
  const now = new Date();
  const hours = now.getHours() + now.getMinutes() / 60;
  if (hours <= WORKDAY_START_HOUR) return 0;
  if (hours >= WORKDAY_END_HOUR) return 100;
  return ((hours - WORKDAY_START_HOUR) / WORKDAY_SPAN_HOURS) * 100;
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Convert a clock hour (decimal) to a % position on the bar. */
function hourToPercent(hour: number): number {
  if (hour <= WORKDAY_START_HOUR) return 0;
  if (hour >= WORKDAY_END_HOUR) return 100;
  return ((hour - WORKDAY_START_HOUR) / WORKDAY_SPAN_HOURS) * 100;
}

/** Fun markers on the progress bar */
const BAR_MARKERS: { hour: number; emoji: string; tooltip: string }[] = [
  { hour: 10, emoji: '☕', tooltip: 'Coffee break' },
  { hour: 12.5, emoji: '🍽️', tooltip: 'Lunch 12:30 — 13:45' },
  { hour: 16, emoji: '☕', tooltip: 'Coffee break' },
];

export default function DailyProgressBar({ totalSeconds, timerElapsedMs }: DailyProgressBarProps) {
  const { t } = useTranslation();
  const combinedSeconds = totalSeconds + Math.floor(timerElapsedMs / 1000);
  const percent = Math.min((combinedSeconds / DAILY_TARGET_SECONDS) * 100, 100);
  const isOver = combinedSeconds >= DAILY_TARGET_SECONDS;

  // "Now" marker — updates every minute
  const [nowPercent, setNowPercent] = useState(computeNowPercent);
  useEffect(() => {
    const interval = setInterval(() => setNowPercent(computeNowPercent()), 60_000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
        <Typography variant="h3">{t('kronos.daily.title')}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
          {t('kronos.daily.target', { current: formatHoursMinutes(combinedSeconds) })}
        </Typography>
      </Box>
      <Box sx={{ position: 'relative', pt: 3.5 }}>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{
            height: 12,
            bgcolor: colors.lightGray,
            '& .MuiLinearProgress-bar': {
              bgcolor: 'rgba(237, 200, 163, 0.6)',
              ...(isOver && {
                animation: 'timerGlow 2s ease-in-out infinite',
              }),
            },
            '@keyframes timerGlow': {
              '0%, 100%': { boxShadow: `0 0 8px ${'rgba(237, 200, 163, 0.6)'}` },
              '50%': { boxShadow: `0 0 20px ${'rgba(237, 200, 163, 0.6)'}, 0 0 40px ${'rgba(237, 200, 163, 0.6)'}88` },
            },
          }}
        />
        {/* Fun emoji markers: coffee + lunch */}
        {BAR_MARKERS.map(({ hour, emoji, tooltip }) => (
          <Tooltip key={hour} title={tooltip} placement="top" arrow>
            <Box
              sx={{
                position: 'absolute',
                left: `${hourToPercent(hour)}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: 10,
                lineHeight: 1,
                cursor: 'default',
                opacity: 0.6,
                zIndex: 1,
              }}
            >
              {emoji}
            </Box>
          </Tooltip>
        ))}

        {/* "Now" time marker — vertical line with time label above */}
        {nowPercent > 0 && nowPercent < 100 && (
          <Box
            sx={{
              position: 'absolute',
              left: `${nowPercent}%`,
              top: 0,
              bottom: -2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              transform: 'translateX(-50%)',
              transition: 'left 60s linear',
              zIndex: 2,
            }}
          >
            {/* Time label */}
            <Typography
              variant="caption"
              sx={{
                fontSize: 9,
                fontWeight: 700,
                color: colors.red,
                lineHeight: 1,
                mb: 0.25,
                whiteSpace: 'nowrap',
              }}
            >
              {formatTime(new Date())}
            </Typography>
            {/* Vertical line */}
            <Box
              sx={{
                width: 2,
                flex: 1,
                bgcolor: colors.red,
                opacity: 0.7,
              }}
            />
          </Box>
        )}
        {isOver && (
          <Typography
            sx={{
              position: 'absolute',
              right: -28,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            🔥
          </Typography>
        )}
      </Box>
    </Box>
  );
}
